import { cardMultiplier, type Fixture, type Prediction, type Round } from '@h2h/shared';
import { Check, ArrowRight, Info } from '../../components/ui/icons';
import type { FixtureInsight } from '../../lib/api/insight';
import { formatMultiplier, signedPoints } from '../../lib/format';
import { cn } from '../../lib/cn';
import { BackBar } from './BackBar';
import { FixtureShell } from './FixtureShell';
import { kickoffLabel, topOutcome, topShare } from './helpers';
import { LiveCardSummary } from './LiveCardSummary';
import { MatchStats } from './MatchStats';
import { MatchTimeline } from './MatchTimeline';
import { TeamMark } from './TeamMark';
import { STATE_LABEL } from './types';
import type { FootballMatchDetailVM } from './useFootballMatchDetail';

export function FootballMatchView({
  fixture,
  round,
  leagueName,
  myPick,
  vm,
  insight,
  onBack,
  onLiveBoard,
  embedded = false
}: {
  fixture: Fixture;
  round: Round | undefined;
  leagueName: string;
  myPick: Prediction | undefined;
  vm: FootballMatchDetailVM;
  insight: FixtureInsight | undefined;
  onBack: () => void;
  onLiveBoard: () => void;
  /** Rendered inside an overlay sheet that already provides the dark surface,
   *  scroll and its own back affordance — so skip the FixtureShell + BackBar. */
  embedded?: boolean;
}) {
  const body = (
    <>
      {/* match header */}
      <div className="relative mt-3 overflow-hidden rounded-3xl bg-gradient-to-br from-[#23232C] to-[#0A0A0E] px-[22px] pb-[22px] pt-5 shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.08]">
        <span className="pointer-events-none absolute left-1/2 top-[-70px] h-[200px] w-[240px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.18),transparent_70%)]" />
        <div className="relative">
          <div className="text-center text-[10px] font-bold uppercase tracking-eyebrow text-[#9A9AA4]">
            {leagueName} · {round?.label ?? ''}
          </div>
          <div className="mt-[18px] flex items-center justify-between">
            <TeamMark
              code={vm.home}
              name={fixture.homeTeam}
              logo={fixture.homeLogo}
              flag={vm.nationalTeams}
            />
            <div className="flex flex-col items-center gap-2">
              {vm.final || vm.live ? (
                <div className="flex items-center gap-[10px]">
                  <span className="num text-[44px] font-extrabold leading-none tracking-tight text-white">
                    {vm.showHome}
                  </span>
                  <span className="text-[24px] font-bold text-[#5E5E68]">–</span>
                  <span className="num text-[44px] font-extrabold leading-none tracking-tight text-white">
                    {vm.showAway}
                  </span>
                </div>
              ) : (
                <span className="text-[24px] font-bold text-[#5E5E68]">vs</span>
              )}
              {vm.live ? (
                <span className="inline-flex items-center gap-[6px] rounded-lg border border-accent/35 bg-accent/15 px-[9px] py-1">
                  <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />
                  <span className="text-[11px] font-extrabold text-accent">
                    {fixture.elapsed != null ? `${fixture.elapsed}'` : 'LIVE'}
                  </span>
                </span>
              ) : vm.final ? (
                <span className="text-[11px] font-bold tracking-wide text-white/40">FULL TIME</span>
              ) : (
                <span className="text-[11px] font-medium text-[#7E7E88]">{kickoffLabel(fixture.kickoffAt)}</span>
              )}
            </div>
            <TeamMark
              code={vm.away}
              name={fixture.awayTeam}
              logo={fixture.awayLogo}
              flag={vm.nationalTeams}
            />
          </div>
          {fixture.venue ? (
            <div className="mt-4 text-center text-[11px] font-medium text-[#7E7E88]">{fixture.venue}</div>
          ) : null}
        </div>
      </div>

      {/* live "if it ends now" hero — the glanceable watch-along read */}
      {vm.live && vm.liveCard && <LiveCardSummary live={vm.liveCard} />}

      {/* your prediction — kept directly under the score so you see what you
          backed before any timeline/stats */}
      <div className="flex items-center justify-between pt-[22px]">
        <span className="text-[13px] font-bold tracking-snug text-white">
          {vm.live && myPick ? 'Card breakdown' : 'Your card'}
        </span>
        {myPick && (
          <div className="flex items-baseline gap-[1px] rounded-lg bg-white/10 px-[9px] py-1">
            <span className="text-[10px] font-bold text-white/50">×</span>
            <span className="num text-[13px] font-extrabold leading-none text-white">
              {formatMultiplier(cardMultiplier(myPick.legs ?? []))}
            </span>
          </div>
        )}
      </div>

      {myPick ? (
        <div
          className={cn(
            'mt-3 rounded-xl border p-4',
            vm.final && vm.myPoints != null
              ? vm.myPoints > 0
                ? 'border-[1.5px] border-accent bg-accent/[0.07]'
                : vm.myPoints < 0
                  ? 'border-[1.5px] border-miss bg-miss/[0.07]'
                  : 'border-white/[0.08] bg-white/[0.05]'
              : 'border-white/[0.08] bg-white/[0.05]'
          )}
        >
          <div className="flex flex-col gap-[11px]">
            {vm.rows.map((row) => (
              <div key={row.label} className="flex items-center gap-[11px]">
                <span
                  className={cn(
                    'grid h-6 w-6 shrink-0 place-items-center rounded-full',
                    row.state === 'hit' && 'bg-accent',
                    row.state === 'miss' && 'bg-miss',
                    row.state === 'ontrack' && 'border-2 border-accent',
                    row.state === 'offtrack' && 'border-2 border-miss',
                    row.state === 'open' && 'border-2 border-white/30'
                  )}
                >
                  {row.state === 'hit' && (
                    <Check width={13} height={13} strokeWidth={3.5} className="text-[#06371F]" />
                  )}
                  {row.state === 'ontrack' && <span className="h-[7px] w-[7px] rounded-full bg-accent" />}
                </span>
                <span className="flex-1 text-[13px] font-semibold text-white/85">
                  {row.label} · <span className="font-extrabold text-white">{row.value}</span>
                </span>
                <span
                  className={cn(
                    'num rounded-md px-[6px] py-[2px] text-[11px] font-extrabold',
                    row.factor.startsWith('÷') ? 'bg-miss/15 text-miss' : 'bg-white/10 text-white/70'
                  )}
                >
                  {row.factor}
                </span>
                <span
                  className={cn(
                    'text-[12px] font-extrabold',
                    (row.state === 'hit' || row.state === 'ontrack') && 'text-accent',
                    (row.state === 'miss' || row.state === 'offtrack') && 'text-miss',
                    row.state === 'open' && 'text-white/40'
                  )}
                >
                  {STATE_LABEL[row.state]}
                </span>
              </div>
            ))}
          </div>
          {vm.myPoints != null && (
            <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-[15px]">
              <span className="text-[12px] font-semibold text-white/55">Points from this match</span>
              <span
                className={cn(
                  'num text-[24px] font-extrabold tracking-tight',
                  vm.myPoints > 0 && 'text-accent',
                  vm.myPoints < 0 && 'text-miss',
                  vm.myPoints === 0 && 'text-white/70'
                )}
              >
                {signedPoints(vm.myPoints)}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 flex flex-col items-center rounded-xl border border-dashed border-white/15 bg-white/[0.04] px-4 py-6 text-center">
          <p className="text-[13px] font-medium text-white/55">{vm.noPickMessage}</p>
          {vm.noPickAction && (
            <button
              type="button"
              onClick={vm.noPickAction.onClick}
              className={cn(
                'tap mt-4 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-extrabold',
                vm.noPickAction.primary
                  ? 'bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] text-white shadow-[0_10px_20px_-10px_rgba(36,197,106,0.6)]'
                  : 'border border-white/15 bg-white/[0.06] text-white'
              )}
            >
              {vm.noPickAction.label}
              <ArrowRight width={13} height={13} strokeWidth={2.8} />
            </button>
          )}
        </div>
      )}

      {vm.showLiveLink && (
        <button
          type="button"
          onClick={onLiveBoard}
          className="tap mt-3 flex w-full items-center justify-center gap-[7px] rounded-xl border border-white/[0.08] bg-white/[0.05] py-3 text-[13px] font-bold text-white active:bg-white/[0.08]"
        >
          {vm.anyRoundLive && <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />}
          {vm.anyRoundLive ? 'Follow your cards live' : 'See all your cards'}
          <ArrowRight width={13} height={13} strokeWidth={2.8} className="text-white/40" />
        </button>
      )}

      {/* match timeline */}
      {vm.events.length > 0 && <MatchTimeline events={vm.events} live={vm.live} />}

      {/* match statistics */}
      {(vm.final || vm.live) && fixture.statistics && (
        <MatchStats stats={fixture.statistics} live={vm.live} />
      )}

      {/* division crowd read — only useful once you've built a card here */}
      {myPick && insight && insight.sampleSize > 0 && insight.outcome && (
        <>
          <div className="pt-[22px]">
            <span className="text-[13px] font-bold tracking-snug text-white">In your division</span>
          </div>
          <div className="mt-3 flex items-center gap-[13px] rounded-lg border border-white/[0.08] bg-white/[0.05] p-4">
            <div className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-tile bg-white/[0.07]">
              <Info width={20} height={20} strokeWidth={2} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-bold tracking-snug text-white">
                <span className="text-accent">{topShare(insight.outcome)}%</span> backed {topOutcome(insight.outcome)}
              </div>
              <div className="mt-[1px] text-[11px] font-medium text-white/55">
                {insight.topLeg
                  ? `${insight.topLeg.pct}% boosted ${insight.topLeg.label}`
                  : 'No boosts backed yet'}{' '}
                · {insight.sampleSize} of {insight.divisionSize ?? insight.sampleSize} built a card
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <FixtureShell>
      <BackBar onBack={onBack} eyebrow={`${leagueName} · ${round?.label ?? 'Fixture'}`} />
      {body}
    </FixtureShell>
  );
}
